import { useCallback } from 'react'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import {
  CustomPeriodFilter,
  PeriodFilter,
  TDashboardWidget
} from 'types/GlobalWidget'
import { DashboardPageStoreEntry } from 'redux/reducers/DashboardPage'
import type { TSavedDashboardFilterDto } from '@homepal/api-contract'
import { useDashboardPageStateActions } from 'redux/actions/DashboardPage'
import * as UserEvents from 'redux/api/internal/UserEvents'
import { Action } from 'types/GlobalUserEvents'
import {
  useCustomSegmentByMapper,
  useDashboardFilter,
  usePeriodFilter,
  useSavedDashboardFilter
} from 'redux/hooks/DashboardPage'
import { TDashboard } from 'types/GlobalDashboard'
import { useWidgetActions } from 'redux/actions/Widgets'

/**
 * Hook for handling dashboard filter changes
 *
 * @returns Memoized function to handle filter changes
 */
export const useSetDashboardFilter = () => {
  const { updateManyDashboardPageEntires } = useDashboardPageStateActions()
  const { invalidateWidgets } = useWidgetActions()
  const savedDashboardFilter = useSavedDashboardFilter()

  return useCallback(
    (
      newDashboardFilter: DashboardFilter,
      newPeriodFilter?: CustomPeriodFilter
    ) => {
      const updates: DashboardPageStoreEntry[] = [
        { key: 'dashboardFilter', value: newDashboardFilter }
      ]

      if (newPeriodFilter !== undefined) {
        updates.push({ key: 'periodFilter', value: newPeriodFilter })
      }

      if (savedDashboardFilter) {
        // if dashboard filter is changed, then remove selected saved.
        updates.push({ key: 'savedDashboardFilter', value: null })
      }

      updateManyDashboardPageEntires(updates)

      // Invalidate all widgets to trigger re-fetch with new filters
      invalidateWidgets()
    },
    [savedDashboardFilter, invalidateWidgets]
  )
}

/**
 * Hook for resetting all dashboard user inputs
 *
 * @param dashboard - Dashboard metadata
 * @returns Memoized function to reset all dashboard filters
 */
export const useResetDashboardUserInput = (dashboard: TDashboard | null) => {
  const { updateManyDashboardPageEntires } = useDashboardPageStateActions()
  const { invalidateWidgets } = useWidgetActions()

  return useCallback(() => {
    if (!dashboard) return

    updateManyDashboardPageEntires([
      { key: 'dashboardFilter', value: [] },
      { key: 'periodFilter', value: null },
      { key: 'savedDashboardFilter', value: null },
      { key: 'customSegmentByMapper', value: {} }
    ])

    // Invalidate all widgets to trigger re-fetch with reset filters
    invalidateWidgets()

    UserEvents.log({
      action: Action.DASHBOARD_RESETTED,
      context: null,
      dashboard_id: dashboard.id,
      dashboard_group_id: dashboard.dashboard_group_id
    })
  }, [dashboard, invalidateWidgets])
}

/**
 * Hook for handling period filter changes
 *
 * @returns Memoized function to handle period filter changes
 */
export const useSetDashboardPeriodFilter = () => {
  const { updateOneDashboardPageEntry } = useDashboardPageStateActions()
  const { invalidateWidgets } = useWidgetActions()

  return useCallback(
    (period: PeriodFilter | CustomPeriodFilter | null) => {
      updateOneDashboardPageEntry({ key: 'periodFilter', value: period })

      // Invalidate all widgets to trigger re-fetch with new period filter
      invalidateWidgets()
    },
    [invalidateWidgets]
  )
}

/**
 * Hook for handling saved dashboard filter selection
 *
 * @returns Memoized function to handle saved filter selection
 */
export const useSetSavedDashboardFilter = () => {
  const savedDashboardFilter = useSavedDashboardFilter()
  const { updateOneDashboardPageEntry, updateManyDashboardPageEntires } =
    useDashboardPageStateActions()
  const { invalidateWidgets } = useWidgetActions()

  return useCallback(
    (saved: TSavedDashboardFilterDto | null, skipFetch = false) => {
      // deselected = deleted --> do not clear filter
      // or if selected already selected then do nothing
      if (!saved || skipFetch || saved.id === savedDashboardFilter?.id) {
        updateOneDashboardPageEntry({
          key: 'savedDashboardFilter',
          value: saved
        })

        return
      }

      const savedFilters = saved.conditions.flatMap((condition) =>
        condition.values.map((value) => ({
          attribute_option_id: null,
          attribute: condition.attribute_id,
          value
        }))
      )

      updateManyDashboardPageEntires([
        { key: 'savedDashboardFilter', value: saved },
        { key: 'dashboardFilter', value: savedFilters }
      ])

      // Invalidate all widgets to trigger re-fetch with saved filters
      invalidateWidgets()
    },
    [savedDashboardFilter, invalidateWidgets]
  )
}

/**
 * Hook for handling custom widget segment-by changes
 *
 * @param dashboard - Dashboard metadata
 * @returns Memoized function to handle segment-by changes
 */
export const useSetCustomWidgetSegmentBy = (dashboard: TDashboard | null) => {
  const { updateManyDashboardPageEntires } = useDashboardPageStateActions()
  const { invalidateWidgets } = useWidgetActions()

  const customSegmentByMapper = useCustomSegmentByMapper()
  const dashboardFilter = useDashboardFilter()
  const periodFilter = usePeriodFilter()

  return useCallback(
    (
      widget: TDashboardWidget,
      attributeOptionId: string | null,
      dbFilter?: DashboardFilter,
      newPeriodFilter?: CustomPeriodFilter
    ) => {
      if (!dashboard) return

      const newSegmentByMapper = {
        ...customSegmentByMapper,
        [widget.id]: attributeOptionId
      }

      const updates: DashboardPageStoreEntry[] = [
        { key: 'customSegmentByMapper', value: newSegmentByMapper }
      ]

      if (dbFilter !== undefined) {
        updates.push({ key: 'dashboardFilter', value: dbFilter })
      }
      if (newPeriodFilter !== undefined) {
        updates.push({ key: 'periodFilter', value: newPeriodFilter })
      }

      updateManyDashboardPageEntires(updates)

      // Invalidate all widgets to trigger re-fetch with new segment-by
      invalidateWidgets()

      UserEvents.log({
        action: attributeOptionId
          ? Action.CUSTOMIZED_SEGMENT_BY_CHANGED
          : Action.CUSTOMIZED_SEGMENT_BY_CLEARED,
        context: widget.type,
        dashboard_id: dashboard.id,
        dashboard_group_id: dashboard.dashboard_group_id
      })
    },
    [
      dashboard,
      customSegmentByMapper,
      periodFilter,
      dashboardFilter,
      invalidateWidgets
    ]
  )
}
